
import React from "react";
import { SistemaForm } from "@/components/solar/configuracao/SistemaForm";
import { supabase } from "@/integrations/supabase/client";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";

interface SistemasTabProps {
  painelImagePreviewTradicional: string;
  inversorImagePreviewTradicional: string;
  setPainelImagePreviewTradicional: (url: string) => void;
  setInversorImagePreviewTradicional: (url: string) => void;
  setPainelImageTradicional: (file: File | null) => void;
  setInversorImageTradicional: (file: File | null) => void;
  handleFileUpload: (
    e: React.ChangeEvent<HTMLInputElement>, 
    setFile: (file: File | null) => void, 
    setPreview: (url: string) => void, 
    setFormValue: (value: string) => void
  ) => void;
}

export const SistemasTab = ({
  painelImagePreviewTradicional,
  inversorImagePreviewTradicional,
  setPainelImagePreviewTradicional,
  setInversorImagePreviewTradicional,
  setPainelImageTradicional,
  setInversorImageTradicional,
  handleFileUpload
}: SistemasTabProps) => {
  // Mutation to save system data to Supabase
  const { mutate: saveSystemToSupabase } = useMutation({
    mutationFn: async (formData: any) => {
      const { data: existingConfig, error: fetchError } = await supabase
        .from('configuracoes_sistemas')
        .select('id')
        .eq('nome', formData.nome)
        .maybeSingle();

      if (fetchError) throw fetchError;

      // Prepare data for saving
      const configData = {
        nome: formData.nome,
        marca_padrao_painel: formData.marca_painel,
        modelo_padrao_painel: formData.modelo_painel,
        foto_padrao_painel: formData.foto_painel,
        marca_padrao_inversor: formData.marca_inversor,
        modelo_padrao_inversor: formData.modelo_inversor,
        foto_padrao_inversor: formData.foto_inversor,
        eficiencia: formData.eficiencia || 0.85,
        valor_por_kwp: formData.valor_por_kwp || 4500,
        fator_capacidade: formData.fator_capacidade || 0.8,
        fator_simultaneidade: formData.fator_simultaneidade || 0.85,
        perdas: formData.perdas || 0.15,
        estruturas: formData.estruturas || []
      };

      let result;
      if (existingConfig) {
        // Update existing record
        const { data, error } = await supabase
          .from('configuracoes_sistemas')
          .update(configData)
          .eq('id', existingConfig.id)
          .select();
        
        if (error) throw error;
        result = data?.[0];
      } else {
        // Insert new record
        const { data, error } = await supabase
          .from('configuracoes_sistemas')
          .insert(configData)
          .select();
        
        if (error) throw error;
        result = data?.[0];
      }

      return result;
    },
    onSuccess: () => {
      toast.success("Sistema solar salvo com sucesso!");
    },
    onError: (error) => {
      console.error("Erro ao salvar sistema:", error);
      toast.error("Erro ao salvar sistema solar");
    }
  });

  const handleFormSubmit = (formData: any) => {
    // Save to Supabase
    saveSystemToSupabase(formData);
    
    // Also save to localStorage for backward compatibility
    try {
      localStorage.setItem("sistemaConfigData", JSON.stringify(formData));
    } catch (error) {
      console.error("Erro ao salvar no localStorage:", error);
    }
  };

  return (
    <SistemaForm 
      tipo="tradicional"
      painelImagePreview={painelImagePreviewTradicional}
      inversorImagePreview={inversorImagePreviewTradicional}
      setPainelImagePreview={setPainelImagePreviewTradicional}
      setInversorImagePreview={setInversorImagePreviewTradicional}
      setPainelImage={setPainelImageTradicional}
      setInversorImage={setInversorImageTradicional}
      handleFileUpload={handleFileUpload}
      onSubmit={handleFormSubmit}
    />
  );
};
